import { Form } from "@unform/web";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { messageStatusLabel } from "Helpers/MessageStatus";
import moment from "Helpers/MomentCompat";
import { showNotification } from "Helpers/Toast";

import UseFile from "Hooks/UseFile";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";
import Select from "Components/Global/Form/Select";

import * as S from "../Schedule/styles";

/*
 * Sessão MATERIAIS do PROPONENTE (R4, 19/09/2026).
 *
 * Para o patrocinador esta aba se chama "Materiais Aprovados" e é só leitura
 * (componente ApprovedMaterials). Para o proponente ela é o lugar de ENVIAR o
 * que foi solicitado a ele — até aqui a única forma era digitar um texto na
 * conversa e anexar arquivo, e o campo de mensagem recusa arquivo sem texto.
 *
 * O material é uma mensagem com arquivo (D13): nasce "Pendente", o admin libera
 * e, aprovado, aparece em "Materiais Aprovados" do patrocinador sem nenhum
 * passo extra.
 *
 * ⚠️ Área comum é ESCURA: os componentes globais já têm variant default "dark".
 * Não passar variant="light" aqui.
 */
const Materials = ({ projectId, project }) => {
  const { api } = UseServices();
  const { uploadFile } = UseFile();

  const [materials, setMaterials] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [saving, setSaving] = useState(false);

  // Vem do detalhe do projeto, que a tela já buscou — sem requisição nova.
  const sponsorOptions = (project?.contributed_links || []).map((link) => ({
    label: link.label,
    value: link.project_sponsor_id,
  }));

  const getMaterials = () => {
    api
      .post("/projects/materials/list", { project_id: projectId })
      .then((response) => setMaterials(response.data?.materials || []))
      .catch((error) => HTTPErrorCallback(error));
  };

  useEffect(() => {
    if (projectId) getMaterials();
    // eslint-disable-next-line
  }, [projectId]);

  const openCreate = () => {
    setTitle("");
    setDescription("");
    setFile(null);
    // Um patrocinador só: já vem escolhido, não há o que decidir.
    setSelectedSponsor(sponsorOptions.length === 1 ? sponsorOptions[0] : null);
    setShowForm(true);
  };

  const handleFileUpload = (e) => {
    const selected = e.target.files[0];

    if (!selected) return;

    setFile(selected);
  };

  const submit = async () => {
    if (!title || title.trim().length < 2)
      return showNotification(
        "danger",
        "Atenção",
        "Informe o nome do material.",
      );

    if (!selectedSponsor)
      return showNotification(
        "danger",
        "Atenção",
        "Selecione o patrocinador de destino.",
      );

    if (!file) return showNotification("danger", "Atenção", "Anexe o arquivo.");

    setSaving(true);

    try {
      const file_src = await uploadFile(file);

      await api.post("/projects/materials/send", {
        project_id: projectId,
        project_sponsor_id: selectedSponsor.value,
        title: title.trim(),
        description: description?.trim() || null,
        file_src,
        file_name: file.name,
      });

      showNotification("success", "Sucesso!", "Material enviado com sucesso!");

      setShowForm(false);
      getMaterials();
    } catch (error) {
      HTTPErrorCallback(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <S.Wrapper>
      <S.Section>
        <span>Materiais</span>
        <hr />
      </S.Section>

      {!sponsorOptions.length ? (
        <span>
          Nenhum patrocinador com aporte ativo neste projeto — não há para quem
          enviar materiais.
        </span>
      ) : null}

      {sponsorOptions.length ? (
        <S.Buttons>
          <Button
            type="button"
            text={showForm ? "Fechar" : "Enviar Material"}
            outline={showForm}
            onClick={() => (showForm ? setShowForm(false) : openCreate())}
          />
        </S.Buttons>
      ) : null}

      {showForm ? (
        <S.FormBlock>
          <Form autoComplete="off">
            <Input
              name="material_title"
              label="Nome do material"
              placeholder="Ex.: Logo aplicada no palco"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            <Input
              name="material_description"
              label="Descrição"
              placeholder="Detalhe o que está sendo entregue"
              type="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <Select
              name="project_sponsor_id"
              label="Patrocinador de destino"
              placeholder="Selecione o patrocinador"
              options={sponsorOptions}
              value={selectedSponsor}
              onChange={setSelectedSponsor}
              maxWidth="100%"
              required
            />

            <input
              aria-label="Arquivo do material"
              type="file"
              onChange={handleFileUpload}
            />

            <Button
              type="button"
              text={saving ? "Enviando..." : "Enviar"}
              disabled={saving}
              onClick={submit}
            />
          </Form>
        </S.FormBlock>
      ) : null}

      {!materials.length ? <span>Nenhum material enviado.</span> : null}

      {materials.map((material) => (
        <S.Event key={material.id}>
          <h2>{material.title || material.file_name || "Material"}</h2>
          <span>
            Para {material.sponsor_name || "—"} em{" "}
            {moment(material.created_at).format("DD/MM/YYYY")} —{" "}
            {/* O material É uma mensagem: mesmo rótulo da sessão Mensagens. */}
            {messageStatusLabel(material.status)}
          </span>
          {material.description ? <p>{material.description}</p> : null}
          <a href={material.file_src} target="_blank" rel="noreferrer">
            Abrir arquivo
          </a>
        </S.Event>
      ))}
    </S.Wrapper>
  );
};

export default Materials;
